import type { Schemas } from '@argzombie/api-client';
import { EFFECT_BLOCKS, EFFECT_TYPES_V1, type EffectType } from '../story/effects';
import type { CondDef, EffectDef } from '../story/types';
import { isBlank, num, type Err, type Ids } from './common';
import { idSet, sectionEntries } from './sections';
import type { Bundle } from './types';

/*
 * The scenario layer's vocabulary (shared-cs `Scenarios/ScenarioVocabulary.cs`, `Encounters/TicketKinds.cs`, `Interiors/SetPiecePlacer.cs`,
 * `Missions/StageRules.cs`, `World/WorldFlags.cs`, `World/MapBadges.cs`), in the C# order.
 */

/** `TicketKinds.All`; `EncounterLayouts.IsSupported` plays interior, comic and (S3-ARS) arena (overwatch: S3-OWS; multi_floor reserved). */
export const TICKET_KINDS = ['interior', 'arena', 'overwatch', 'comic', 'multi_floor'] as const;
export const PLAYABLE_KINDS = ['interior', 'comic', 'arena'] as const;
export const OBJECTIVE_TYPES = [
  'find',
  'free',
  'hold',
  'escort',
  'crescendo',
  'kill_target',
  'reach_exit',
  'no_kill',
  'scripted',
] as const;
export type ObjectiveType = (typeof OBJECTIVE_TYPES)[number];
/** `ObjectiveStates.All` (an objective action may also set `revealed`). */
export const OBJECTIVE_STATES = ['open', 'started', 'done', 'failed'] as const;
export const SCENARIO_EVENT_TYPES = [
  'start',
  'enter_room',
  'pick_up',
  'door',
  'interact',
  'timer',
  'wave_spawned',
  'wave_cleared',
  'actor_health',
  'actor_dead',
  'reached',
  'objective',
  'horde_level',
  'building_clear',
  'beat_outcome',
  'var',
  'order',
  'team_disp',
  'wind',
  'kill',
  'search',
  'revive',
  'treat',
  'near',
  'grab',
] as const;
/** `ScenarioEventTypes.Tutorial`: heard only by a tutorial scenario (Day Zero). */
export const TUTORIAL_EVENT_TYPES = ['order', 'team_disp', 'wind', 'kill', 'search', 'revive', 'treat', 'near', 'grab'] as const;
export type ScenarioEventType = (typeof SCENARIO_EVENT_TYPES)[number];
/** `ScenarioEventTypes.LoseTypes`. */
export const LOSE_TYPES = ['actor_dead', 'timer', 'objective', 'interact'] as const;
export const SCENARIO_ACTION_TYPES = [
  'beat',
  'spawn',
  'wave',
  'horde_fill',
  'objective',
  'actor',
  'door',
  'lights',
  'timer',
  'caption',
  'bark',
  'win',
  'lose',
  'effect',
  'set_flag',
  'recruit',
  'container',
  'hud',
] as const;
/** `ScenarioDoorOps.All` (card #490: `sealed` / `unsealed` are un-forceable; `seal` is the legacy word for `lock`). */
export const DOOR_OPS = ['lock', 'unlock', 'open', 'seal', 'sealed', 'unsealed'] as const;
/** `ScenarioSwitch.All`: the `container` and `hud` actions' set. */
export const SWITCH_STATES = ['on', 'off'] as const;
/** `ScenarioHudControls.All` (card #490): the HUD controls a scenario's `hud` action hides or shows. Client only. */
export const HUD_CONTROLS = [
  'order_ring',
  'team_ring',
  'swap',
  'loot',
  'crouch',
  'throw',
  'exit',
  'squad_strip',
  'you_ring',
] as const;
export type ScenarioActionType = (typeof SCENARIO_ACTION_TYPES)[number];
/** `ScenarioActionTypes.Server`: applied by the server from content after the verdict (the only ones onWin/onLose may hold). */
export const SERVER_ACTIONS = ['effect', 'set_flag', 'recruit'] as const;
/** `ScenarioLogEvents.All`: what a report's log (and the dry run's script) holds. */
export const LOG_EVENTS = [
  'enter',
  'found',
  'pick_up',
  'door',
  'interact',
  'free_start',
  'free_done',
  'free_cancel',
  'wave_spawned',
  'wave_cleared',
  'actor_health',
  'actor_dead',
  'escort_start',
  'reached',
  'innocent_killed',
  'horde_level',
  'building_clear',
  'beat_outcome',
] as const;
export const ACTOR_ROLES = ['rescuee', 'escort', 'target', 'innocent', 'hostile'] as const;
export const ACTOR_STATES = [
  'idle',
  'following',
  'staying',
  'cowering',
  'fled',
  'turned',
  'joined',
  'dead',
] as const;
export const ANCHOR_KINDS = [
  'npc_mark',
  'wave_spawn',
  'container',
  'choke',
  'fortify',
  'mark',
  'exit',
] as const;
export const SET_PIECE_PLACEMENTS = ['deepest', 'near_entrance', 'entrance', 'any'] as const;
/** `SetPieceDoorPolicies.All`: '' keeps the doors, `one` seals all but the front, `any` = ''. */
export const DOOR_POLICIES = ['', 'one', 'any'] as const;
export const SET_PIECE_ROTATIONS = [0, 90, 180, 270] as const;
/** `SetPiecePlacer.MinSide` / `MaxSide` (m). */
export const SET_PIECE_SIDE = { min: 4, max: 8 } as const;
/** `InteriorGenerator.MaxRooms`. */
export const INTERIOR_MAX_ROOMS = 16;
export const WORLD_FLAG_TYPES = ['bool', 'int', 'string', 'enum'] as const;
export const WORLD_FLAG_SCOPES = ['run', 'account'] as const;
/** `MapBadgeKinds.All`. */
export const MAP_BADGE_KINDS = [
  'mission',
  'tip',
  'occupied',
  'rescue',
  'siege',
  'keypart',
  'lure',
  'perch',
  'story',
  'flag',
] as const;
export const STAGE_LOCATION_TYPES = [
  'building',
  'same_building',
  'tip_target',
  'trigger_code',
  'home',
  'camp',
  'base',
  'anywhere',
] as const;
export const STAGE_NEAR = ['player', 'team', 'home'] as const;
/** `ScenarioConditions.Namespaces`: what a scenario trigger's `if` can read offline. */
export const SCENARIO_FACT_NAMESPACES = [
  'var.',
  'objective.',
  'actor.',
  'found.',
  'timer.',
  'raid.',
] as const;
/** `ScenarioLimits`. */
export const SCENARIO_LIMITS = {
  maxObjectives: 8,
  maxTriggers: 24,
  /** Day Zero: a tutorial arc's scenario. */
  maxTutorialObjectives: 40,
  maxTutorialTriggers: 80,
  maxActionsPerTrigger: 6,
  maxActors: 4,
  maxSetPieces: 2,
  maxWaves: 8,
  minTimerS: 5,
  maxTimerS: 900,
  maxStages: 6,
  maxFlagValueLength: 32,
  maxFlagKeyLength: 64,
  maxWaveSize: 25,
} as const;

/** The generated scenario types; mission stage locations gain `ref` (same_building) and `triggerId` (trigger_code) in S1-SCN. */
export type ScenarioDef = Schemas['ScenarioDef'];
export type ObjectiveDef = Schemas['ObjectiveDef'];
export type ScenarioTriggerDef = Schemas['ScenarioTriggerDef'];
export type ScenarioEventDef = Schemas['ScenarioEventDef'];
export type ScenarioActionDef = Schemas['ScenarioActionDef'];
export type ScenarioActorDef = Schemas['ScenarioActorDef'];
export type SetPieceDef = Schemas['SetPieceDef'];
export type SetPieceAnchorDef = Schemas['SetPieceAnchorDef'];
export type WorldFlagDef = Schemas['WorldFlagDef'];
export type StageLocationDef = Schemas['MissionStageLocationDef'] & {
  ref?: string;
  triggerId?: string;
};
export type MissionStageDef = Omit<Schemas['MissionStageDef'], 'location' | 'next'> & {
  location?: StageLocationDef;
  next?: string | null;
};

const oneOf = (list: readonly string[], v: string | null | undefined) => list.includes(v ?? '');
const normalizeKind = (k: string | null | undefined) => (k ? k : 'interior');
/** `BuildingKinds.Normalize`: lower case, spaces and dashes to _, cut at ';', at most 32 characters. */
export const normalizeBuildingKind = (v: string | null | undefined) => {
  if (!v) return '';
  let s = v.trim().toLowerCase().replace(/ /g, '_').replace(/-/g, '_');
  const semi = s.indexOf(';');
  if (semi >= 0) s = s.slice(0, semi);
  return s.length > 32 ? s.slice(0, 32) : s;
};

/** `WorldFlags.IsValidKey` / `IsValidPattern`: 2+ dot-separated `[a-z0-9_]` segments (patterns may use `{name}`), ≤ 64 characters. */
export function isValidFlagKey(key: string | null | undefined, pattern: boolean) {
  if (!key || key.length > SCENARIO_LIMITS.maxFlagKeyLength) return false;
  const parts = key.split('.');
  if (parts.length < 2) return false;
  return parts.every(
    (p) =>
      p.length > 0 &&
      ((pattern && p.length > 2 && p.startsWith('{') && p.endsWith('}')) || /^[a-z0-9_]+$/.test(p)),
  );
}

/** `WorldFlags.Matches`: `{…}` matches one non-empty segment. */
export function flagMatches(pattern: string, key: string) {
  if (pattern === key) return true;
  const p = pattern.split('.');
  const k = key.split('.');
  if (p.length !== k.length) return false;
  return p.every((s, i) => {
    const ph = s.length > 2 && s.startsWith('{') && s.endsWith('}');
    return ph ? k[i]!.length > 0 : s === k[i];
  });
}

/** `WorldFlags.Find`: an exact key first, then the first matching pattern. */
export function findFlag(catalogue: (WorldFlagDef | null)[] | null | undefined, key: string) {
  if (!key) return undefined;
  const list = (catalogue ?? []).filter((d): d is WorldFlagDef => d != null);
  return list.find((d) => d.key === key) ?? list.find((d) => flagMatches(d.key ?? '', key));
}

/** `WorldFlags.ValueProblem`. */
export function flagValueProblem(def: WorldFlagDef, value: string | null | undefined) {
  if (value == null) return 'a value is required';
  if (value.length > SCENARIO_LIMITS.maxFlagValueLength)
    return `longer than ${SCENARIO_LIMITS.maxFlagValueLength} characters`;
  switch (def.type ?? 'enum') {
    case 'bool':
      return value === 'true' || value === 'false' ? null : 'not true or false';
    case 'int':
      return /^[+-]?\d+$/.test(value.trim()) ? null : 'not a whole number';
    case 'enum':
      return (def.values ?? []).includes(value)
        ? null
        : `not one of ${(def.values ?? []).join(', ')}`;
    default:
      return null;
  }
}

/** `ScenarioConditions.IsKnownFact`. */
export function isScenarioFact(fact: string | null | undefined) {
  if (!fact) return false;
  if (fact === 'raid.innocents_killed') return true;
  if (fact === 'ticket.varHas') return true;
  return SCENARIO_FACT_NAMESPACES.some(
    (n) => n !== 'raid.' && fact.startsWith(n) && fact.length > n.length,
  );
}

/** `ScenarioConditions.Facts`: every fact a condition tree reads. */
export function condFacts(c: CondDef | null | undefined): string[] {
  if (c == null) return [];
  return [
    ...(c.fact ? [c.fact] : []),
    ...(c.all ?? []).flatMap(condFacts),
    ...(c.any ?? []).flatMap(condFacts),
    ...condFacts(c.not),
  ];
}

/** `ScenarioRules.TargetActor`: `actor`, else a target `actor:id`. */
export const targetActor = (o: ObjectiveDef) =>
  o.actor ? o.actor : (o.target ?? '').startsWith('actor:') ? (o.target ?? '').slice(6) : '';

/** `AnchorKnown`: `setPieceId.anchorId` names a set piece of this scenario and one of its anchors. */
function anchorKnown(pieces: Map<string, SetPieceDef>, anchor: string) {
  const dot = anchor.indexOf('.');
  if (dot <= 0) return false;
  const sp = pieces.get(anchor.slice(0, dot));
  if (!sp) return false;
  const id = anchor.slice(dot + 1);
  return (sp.anchors ?? []).some((a) => a != null && a.id === id);
}

const inside = (x: number, y: number, w: number, d: number) => x >= 0 && y >= 0 && x <= w && y <= d;

function targetKnown(target: string | null | undefined, actors: Set<string>) {
  if (!target) return false;
  if (target.startsWith('actor:')) return actors.has(target.slice(6));
  return (
    (target.startsWith('item:') || target.startsWith('prop:')) &&
    target.indexOf(':') < target.length - 1
  );
}

export type ScenarioRefs = {
  items: Set<string>;
  tables: Set<string>;
  zombies: Set<string>;
  categories: Set<string>;
};

/**
 * The scenario layer's sections (`scenarios`, `setPieces`, `worldFlags`, `missions[].stages`): a port of shared-cs
 * `ContentValidator.Scenarios.cs` (S1-SCN) with the same messages in the same order: ids and references, the limits, objective
 * reachability (no `after` loops), trigger events and action fields, known engine effects, the flag catalogue, and the stage rules.
 * Issue paths are dotted paths inside the entry (`objectives.2.after.0`, `triggers.1.do.0.beatId`); stage issues go on `missions` at
 * the mission's index (`stages.0.location.categories.1`). The server's 500-seed placement sweep isn't ported (it needs
 * `InteriorGenerator`): the set-piece editor's placement preview asks the API for it. The server stays authoritative.
 */
export function validateScenarios(b: Bundle, refs: ScenarioRefs, err: Err, warn: Err, ids: Ids) {
  const scenarios = sectionEntries<ScenarioDef>('scenarios', b.scenarios, err);
  const setPieces = sectionEntries<SetPieceDef>('setPieces', b.setPieces, err);
  const flags = sectionEntries<WorldFlagDef>('worldFlags', b.worldFlags, err);
  ids('scenarios', b.scenarios ?? [], 'scenario');
  ids('setPieces', b.setPieces ?? [], 'set piece');
  // `Ids(flags, f => f.Key, "world flag")`: keys, with the ids wording.
  const keys = new Set<string>();
  for (const [f, i] of flags) {
    const k = f.key ?? '';
    if (isBlank(k)) err('worldFlags', i, 'key', 'A world flag has no id.');
    else if (keys.has(k)) err('worldFlags', i, 'key', `Duplicate world flag id '${k}'.`);
    else keys.add(k);
  }

  const templates = idSet(b.roomTemplates);
  const npcs = idSet(b.npcs);
  const beats = idSet(b.beatChains);
  const triggerIds = idSet(b.triggers);
  // Day Zero: the scenarios a tutorial arc's issues play get the tutorial caps and events.
  const tutorialArcs = new Set((b.arcs ?? []).filter((a) => a?.kind === 'tutorial').map((a) => a!.id ?? ''));
  const tutorialScenarios = new Set<string>();
  for (const m of b.missions ?? [])
    if (m?.story && tutorialArcs.has(m.story.arcId ?? ''))
      for (const st of m.stages ?? []) if (st?.encounter?.scenarioId) tutorialScenarios.add(st.encounter.scenarioId);
  const setPieceById = new Map<string, SetPieceDef>();
  for (const [sp] of setPieces) if (sp.id && !setPieceById.has(sp.id)) setPieceById.set(sp.id, sp);
  const scenarioById = new Map<string, ScenarioDef>();
  for (const [s] of scenarios) if (s.id && !scenarioById.has(s.id)) scenarioById.set(s.id, s);

  for (const [f, i] of flags) worldFlag(f, (p, m) => err('worldFlags', i, p, m));
  for (const [sp, i] of setPieces) setPiece(sp, (p, m) => err('setPieces', i, p, m));
  for (const [s, i] of scenarios) scenario(s, (p, m) => err('scenarios', i, p, m));
  (b.missions ?? []).forEach((m, i) => {
    if (m != null) stages(m.id ?? '', m.stages as MissionStageDef[] | null | undefined, i);
  });

  // Client-only: a set piece no scenario (and no Step 2 holdout, rescue, chance trigger or key part) uses is never swept or played.
  const used = new Set(
    [
      ...scenarios.flatMap(([s]) => (s.setPieces ?? []).map((r) => r?.id ?? '')),
      ...(b.holdouts ?? []).map((h) => h?.setPiece ?? ''),
      ...(b.rescues?.scenarios ?? []).map((r) => r?.setPieceRoomId ?? ''),
      ...(b.chanceEvents?.events ?? []).map((e) => e?.trigger?.setPieceId ?? ''),
      ...(b.keyParts ?? []).map((k) => k?.setPieceId ?? ''),
      ...(b.keyPartRules?.virtualSites ?? []).map((v) => v?.setPieceId ?? ''),
    ].filter(Boolean),
  );
  for (const [sp, i] of setPieces)
    if (sp.id && !used.has(sp.id))
      warn(
        'setPieces',
        i,
        '',
        `Set piece '${sp.id}' isn't used by any scenario (the publish sweep skips it).`,
      );

  function worldFlag(f: WorldFlagDef, fail: (path: string, m: string) => void) {
    const where = `World flag '${f.key ?? ''}'`;
    if (!isValidFlagKey(f.key, true))
      fail(
        'key',
        `${where} needs a key of dot-separated a-z, 0-9 and _ segments ({name} placeholders allowed), at most ${SCENARIO_LIMITS.maxFlagKeyLength} characters.`,
      );
    const type = f.type ?? 'enum';
    if (!oneOf(WORLD_FLAG_TYPES, type)) fail('type', `${where} has unknown type '${type}'.`);
    if (type === 'enum' && !(f.values ?? []).length)
      fail('values', `${where} needs values (it's an enum).`);
    (f.values ?? []).forEach((v, j) => {
      if (!v || v.length > SCENARIO_LIMITS.maxFlagValueLength)
        fail(
          `values.${j}`,
          `${where} has a value that's empty or longer than ${SCENARIO_LIMITS.maxFlagValueLength} characters.`,
        );
    });
    const scope = f.scope ?? 'run';
    if (!oneOf(WORLD_FLAG_SCOPES, scope)) fail('scope', `${where} has unknown scope '${scope}'.`);
    if ((f.key ?? '').startsWith('arc.') && scope !== 'account')
      fail('scope', `${where} is a story-arc flag: its scope must be account.`);
    if (f.badge && !oneOf(MAP_BADGE_KINDS, f.badge))
      fail('badge', `${where} has unknown badge '${f.badge}'.`);
  }

  function setPiece(sp: SetPieceDef, fail: (path: string, m: string) => void) {
    const where = `Set piece '${sp.id ?? ''}'`;
    if (isBlank(sp.name)) fail('name', `${where} needs a name.`);
    const w = num(sp.footprint?.w);
    const d = num(sp.footprint?.d);
    const { min, max } = SET_PIECE_SIDE;
    if (w < min || w > max || d < min || d > max)
      fail('footprint', `${where} needs a footprint from ${min}x${min} to ${max}x${max} m.`);
    (sp.rotations ?? []).forEach((r, k) => {
      if (![0, 90, 180, 270].includes(r))
        fail(`rotations.${k}`, `${where} has rotation ${r} (use 0, 90, 180 or 270).`);
    });
    (sp.categories ?? []).forEach((c, k) => {
      if (!refs.categories.has(c))
        fail(`categories.${k}`, `${where} uses unknown category '${c}'.`);
    });
    if (sp.templateId && !templates.has(sp.templateId))
      fail('templateId', `${where} uses unknown room template '${sp.templateId}'.`);
    if ((sp.placementVersion ?? 1) < 1)
      fail('placementVersion', `${where} needs placementVersion >= 1.`);
    const anchors = new Set<string>();
    (sp.anchors ?? []).forEach((a, j) => {
      if (a == null) {
        fail(`anchors.${j}`, `${where} has an empty anchor.`);
        return;
      }
      const id = a.id ?? '';
      if (!id || anchors.has(id))
        fail(`anchors.${j}.id`, `${where} needs unique anchor ids ('${id}').`);
      anchors.add(id);
      if (!oneOf(ANCHOR_KINDS, a.kind))
        fail(`anchors.${j}.kind`, `${where} anchor '${id}' has unknown kind '${a.kind ?? ''}'.`);
      if (!inside(num(a.x), num(a.y), w, d))
        fail(`anchors.${j}`, `${where} anchor '${id}' is outside the footprint.`);
    });
    (sp.props ?? []).forEach((p, j) => {
      if (p == null) return;
      if (isBlank(p.model)) fail(`props.${j}.model`, `${where} has a prop with no model.`);
      if (!inside(num(p.x), num(p.y), w, d))
        fail(`props.${j}`, `${where} prop '${p.model ?? ''}' is outside the footprint.`);
    });
    (sp.zombies ?? []).forEach((z, j) => {
      if (z == null) return;
      if (!refs.zombies.has(z.zombieId ?? ''))
        fail(`zombies.${j}.zombieId`, `${where} uses unknown zombie '${z.zombieId ?? ''}'.`);
      if (!inside(num(z.x), num(z.y), w, d))
        fail(`zombies.${j}`, `${where} zombie '${z.zombieId ?? ''}' is outside the footprint.`);
    });
  }

  function scenario(s: ScenarioDef, fail: (path: string, m: string) => void) {
    const tutorial = tutorialScenarios.has(s.id ?? '');
    const where = `Scenario '${s.id ?? ''}'`;
    const kind = normalizeKind(s.kind);
    if (!oneOf(TICKET_KINDS, kind)) fail('kind', `${where} has unknown kind '${s.kind ?? ''}'.`);
    else if (!oneOf(PLAYABLE_KINDS, kind))
      fail('kind', `${where} has kind '${kind}', which can't be played yet (interior or comic).`);
    if (isBlank(s.title)) fail('title', `${where} needs a title.`);

    const building = s.building ?? {};
    (building.categories ?? []).forEach((c, k) => {
      if (!refs.categories.has(c))
        fail(`building.categories.${k}`, `${where} targets unknown category '${c}'.`);
    });
    (building.kinds ?? []).forEach((k, n) => {
      if (normalizeBuildingKind(k) !== k || k.length === 0)
        fail(`building.kinds.${n}`, `${where} has building kind '${k}' (use lower case and _).`);
    });
    const minRooms = num(building.minRooms);
    if (minRooms < 0 || minRooms > INTERIOR_MAX_ROOMS)
      fail('building.minRooms', `${where} needs minRooms in 0..${INTERIOR_MAX_ROOMS}.`);

    // Hidden vars (S1-INT): rolled at issue into hidden.<id> (mirrors shared-cs).
    const hiddenIds = new Set<string>();
    (s.hidden ?? []).forEach((h, n) => {
      if (h == null || isBlank(h.id)) {
        fail(`hidden.${n}`, `${where} has a hidden var with no id.`);
        return;
      }
      const id = h.id ?? '';
      if (hiddenIds.has(id)) fail(`hidden.${n}.id`, `${where} has a duplicate hidden var '${id}'.`);
      hiddenIds.add(id);
      const deal = (h as { deal?: { from?: string[] | null; count?: number } | null }).deal;
      if (deal != null) {
        const from = deal.from ?? [];
        const hw = `${where} hidden var '${id}'`;
        if (from.length === 0) fail(`hidden.${n}.deal`, `${hw} deals from an empty list.`);
        if (from.some((x) => !x?.trim())) fail(`hidden.${n}.deal`, `${hw} deals an empty id.`);
        if (new Set(from).size !== from.length) fail(`hidden.${n}.deal`, `${hw} deals a value twice.`);
        const count = deal.count ?? 1;
        if (count < 1 || count > from.length) fail(`hidden.${n}.deal`, `${hw} deals ${count} (1..${from.length}).`);
        if ((h.cases ?? []).length > 0) fail(`hidden.${n}.cases`, `${hw} has a deal and cases (one or the other).`);
        return;
      }
      const picks = (h.cases ?? []).some((c) => (c?.pick ?? []).length > 0);
      if (!picks) fail(`hidden.${n}.cases`, `${where} hidden var '${id}' needs a case with a pick.`);
    });

    // Set pieces.
    const pieces = new Map<string, SetPieceDef>();
    const spRefs = s.setPieces ?? [];
    if (spRefs.length > SCENARIO_LIMITS.maxSetPieces)
      fail(
        'setPieces',
        `${where} has ${spRefs.length} set pieces (at most ${SCENARIO_LIMITS.maxSetPieces}).`,
      );
    if (spRefs.length > 0 && kind !== 'interior')
      fail('setPieces', `${where} is a ${kind} scenario: only interiors have set pieces.`);
    spRefs.forEach((r, k) => {
      if (r == null) return;
      const at = (f: string) => `setPieces.${k}.${f}`;
      const sp = setPieceById.get(r.id ?? '');
      if (!sp) {
        fail(at('id'), `${where} uses unknown set piece '${r.id ?? ''}'.`);
        return;
      }
      if (pieces.has(sp.id ?? '')) fail(at('id'), `${where} uses set piece '${sp.id}' twice.`);
      pieces.set(sp.id ?? '', sp);
      const placement = r.placement ?? 'any';
      if (!oneOf(SET_PIECE_PLACEMENTS, placement))
        fail(at('placement'), `${where} places '${r.id}' by unknown rule '${placement}'.`);
      if (!oneOf(DOOR_POLICIES, r.doorPolicy ?? ''))
        fail(at('doorPolicy'), `${where} has unknown doorPolicy '${r.doorPolicy ?? ''}'.`);
      const fits = sp.categories ?? [];
      if (fits.length > 0) {
        const cats = building.categories ?? [];
        if (cats.length === 0)
          fail(
            at('id'),
            `${where} can be any category, but set piece '${sp.id}' only fits ${fits.join(', ')}.`,
          );
        else
          for (const c of cats)
            if (!fits.includes(c))
              fail(
                at('id'),
                `${where} targets category '${c}', which set piece '${sp.id}' doesn't fit.`,
              );
      }
    });

    // Actors.
    const actors = new Set<string>();
    const actorList = s.actors ?? [];
    if (actorList.length > SCENARIO_LIMITS.maxActors)
      fail(
        'actors',
        `${where} has ${actorList.length} actors (at most ${SCENARIO_LIMITS.maxActors}).`,
      );
    actorList.forEach((a, j) => {
      if (a == null) return;
      const at = (f: string) => `actors.${j}.${f}`;
      const id = a.id ?? '';
      if (!id || actors.has(id)) fail(at('id'), `${where} needs unique actor ids ('${id}').`);
      actors.add(id);
      if (!oneOf(ACTOR_ROLES, a.role))
        fail(at('role'), `${where} actor '${id}' has unknown role '${a.role ?? ''}'.`);
      const health = a.health ?? 100;
      if (health < 1 || health > 1000)
        fail(at('health'), `${where} actor '${id}' needs health in 1..1000.`);
      if (a.npcId && !npcs.has(a.npcId))
        fail(at('npcId'), `${where} actor '${id}' casts unknown npc '${a.npcId}'.`);
      if (a.anchor && !anchorKnown(pieces, a.anchor))
        fail(
          at('anchor'),
          `${where} actor '${id}' stands at unknown anchor '${a.anchor}' (setPiece.anchor of this scenario).`,
        );
    });

    // Objectives: the first of each id, in list order (the C# dictionary).
    const objectives = new Map<string, [ObjectiveDef, number]>();
    const objList = s.objectives ?? [];
    const maxObjectives = tutorial ? SCENARIO_LIMITS.maxTutorialObjectives : SCENARIO_LIMITS.maxObjectives;
    if (objList.length > maxObjectives)
      fail('objectives', `${where} has ${objList.length} objectives (at most ${maxObjectives}).`);
    if (objList.length === 0 && kind === 'interior')
      fail('objectives', `${where} needs at least one objective.`);
    objList.forEach((o, j) => {
      if (o == null) return;
      const id = o.id ?? '';
      if (!id || objectives.has(id))
        fail(`objectives.${j}.id`, `${where} needs unique objective ids ('${id}').`);
      else objectives.set(id, [o, j]);
    });
    let waves = 0;
    for (const [o, j] of objectives.values()) {
      objective(where, o, j, objectives, actors, pieces, fail);
      if (o.type === 'hold' || o.type === 'crescendo') waves += Math.max(0, num(o.waves));
    }
    if (waves > SCENARIO_LIMITS.maxWaves)
      fail('objectives', `${where} scripts ${waves} waves (at most ${SCENARIO_LIMITS.maxWaves}).`);
    afterCycles(where, objectives, fail);

    // Scenario triggers.
    const triggers = s.triggers ?? [];
    const maxTriggers = tutorial ? SCENARIO_LIMITS.maxTutorialTriggers : SCENARIO_LIMITS.maxTriggers;
    if (triggers.length > maxTriggers)
      fail('triggers', `${where} has ${triggers.length} scenario triggers (at most ${maxTriggers}).`);
    const timers = new Set<string>();
    let canWin = false;
    for (const t of triggers)
      for (const a of t?.do ?? []) {
        if (a?.type === 'timer' && a.id) timers.add(a.id);
        if (a?.type === 'win') canWin = true;
      }
    const ctx = { objectives, actors, pieces, timers };
    const triggerIdsSeen = new Set<string>();
    triggers.forEach((t, j) => {
      if (t == null) return;
      const base = `triggers.${j}`;
      const tw = `${where} scenario trigger '${t.id ?? ''}'`;
      if (!t.id || triggerIdsSeen.has(t.id))
        fail(`${base}.id`, `${where} needs unique scenario trigger ids ('${t.id ?? ''}').`);
      triggerIdsSeen.add(t.id ?? '');
      event(tw, t.on, `${base}.on`, ctx, fail, false);
      if (
        t.on != null &&
        !tutorial &&
        ((TUTORIAL_EVENT_TYPES as readonly string[]).includes(t.on.type ?? '') || t.on.state === 'shut')
      )
        fail(`${base}.on`, `${tw} listens for Day Zero event '${t.on.type ?? ''}', which only a tutorial scenario hears.`);
      for (const fact of condFacts(t.if as CondDef | null | undefined))
        if (!isScenarioFact(fact))
          fail(
            `${base}.if`,
            `${tw} reads unknown fact '${fact}' (var., objective., actor., found., timer., raid.innocents_killed, ticket.varHas).`,
          );
      const actions = t.do ?? [];
      if (actions.length === 0 || actions.length > SCENARIO_LIMITS.maxActionsPerTrigger)
        fail(`${base}.do`, `${tw} needs 1..${SCENARIO_LIMITS.maxActionsPerTrigger} actions.`);
      actions.forEach((a, k) => action(tw, a, `${base}.do.${k}`, ctx, fail, false));
    });

    (s.lose ?? []).forEach((l, k) => {
      if (l == null) return;
      if (!oneOf(LOSE_TYPES, l.type))
        fail(
          `lose.${k}.type`,
          `${where} has a lose rule of unknown type '${l.type ?? ''}' (actor_dead, timer, objective, interact).`,
        );
      else event(`${where} lose rule`, l, `lose.${k}`, ctx, fail, true);
    });
    (s.onWin ?? []).forEach((a, k) => action(`${where} onWin`, a, `onWin.${k}`, ctx, fail, true));
    (s.onLose ?? []).forEach((a, k) =>
      action(`${where} onLose`, a, `onLose.${k}`, ctx, fail, true),
    );

    const requiredGoal = [...objectives.values()].some(
      ([o]) => (o.required ?? true) && o.type !== 'no_kill',
    );
    if (kind === 'interior' && !requiredGoal && !canWin)
      fail('', `${where} can never be won (no required objective and no win action).`);
  }

  type Ctx = {
    objectives: Map<string, [ObjectiveDef, number]>;
    actors: Set<string>;
    pieces: Map<string, SetPieceDef>;
    timers: Set<string>;
  };

  function objective(
    where: string,
    o: ObjectiveDef,
    j: number,
    objectives: Map<string, [ObjectiveDef, number]>,
    actors: Set<string>,
    pieces: Map<string, SetPieceDef>,
    fail: (path: string, m: string) => void,
  ) {
    const ow = `${where} objective '${o.id ?? ''}'`;
    const at = (f: string) => `objectives.${j}.${f}`;
    if (!oneOf(OBJECTIVE_TYPES, o.type)) {
      fail(at('type'), `${ow} has unknown type '${o.type ?? ''}'.`);
      return;
    }
    if (isBlank(o.label)) fail(at('label'), `${ow} needs a label.`);
    (o.after ?? []).forEach((a, k) => {
      if (!objectives.has(a)) fail(at(`after.${k}`), `${ow} comes after unknown objective '${a}'.`);
      if (a === o.id) fail(at(`after.${k}`), `${ow} comes after itself.`);
    });
    const actor = targetActor(o);
    const actorPath = o.actor ? 'actor' : 'target';
    const holdS = num(o.holdS);
    const n = num(o.waves);
    switch (o.type) {
      case 'find':
        if (!targetKnown(o.target, actors))
          fail(
            at('target'),
            `${ow} needs a target: actor:<id> of this scenario, item:<id> or prop:<id>.`,
          );
        break;
      case 'free':
        if (!o.target)
          fail(at('target'), `${ow} needs a target (door:<tag>, prop:<id> or actor:<id>).`);
        if (holdS < 0.5 || holdS > 120) fail(at('holdS'), `${ow} needs holdS in 0.5..120.`);
        break;
      case 'hold':
      case 'crescendo': {
        const { maxWaves, minTimerS, maxTimerS, maxWaveSize } = SCENARIO_LIMITS;
        if (n < 0 || n > maxWaves) fail(at('waves'), `${ow} needs waves in 0..${maxWaves}.`);
        if (o.type === 'crescendo' && (n < 1 || !o.target))
          fail(at('target'), `${ow} needs a target to interact with and at least 1 wave.`);
        if (o.type === 'hold' && n === 0 && (holdS < minTimerS || holdS > maxTimerS))
          fail(at('holdS'), `${ow} needs waves, or holdS in ${minTimerS}..${maxTimerS}.`);
        const size = num(o.waveSize);
        const growth = num(o.waveGrowth);
        if (
          n > 0 &&
          (size < 1 || size > maxWaveSize || growth < 0 || size + growth * (n - 1) > maxWaveSize)
        )
          fail(
            at('waveSize'),
            `${ow} needs waves of 1..${maxWaveSize} zombies (waveSize + waveGrowth per wave).`,
          );
        if (o.spawn && !anchorKnown(pieces, o.spawn))
          fail(at('spawn'), `${ow} spawns at unknown anchor '${o.spawn}'.`);
        if (o.zone && !pieces.has(o.zone) && !anchorKnown(pieces, o.zone))
          fail(at('zone'), `${ow} holds unknown zone '${o.zone}'.`);
        break;
      }
      case 'escort':
        if (!actors.has(actor)) fail(at(actorPath), `${ow} escorts unknown actor '${actor}'.`);
        if (o.to && o.to !== 'exit' && !anchorKnown(pieces, o.to))
          fail(at('to'), `${ow} goes to unknown mark '${o.to}' (exit or setPiece.anchor).`);
        break;
      case 'kill_target':
        if (!actors.has(actor)) fail(at(actorPath), `${ow} targets unknown actor '${actor}'.`);
        break;
    }
  }

  /** `AfterCycles`: the first objective whose `after` chain loops (one error per scenario, like the server). */
  function afterCycles(
    where: string,
    objectives: Map<string, [ObjectiveDef, number]>,
    fail: (path: string, m: string) => void,
  ) {
    const state = new Map<string, number>(); // 1 visiting, 2 done
    const visit = (id: string): boolean => {
      const s = state.get(id);
      if (s !== undefined) return s === 1;
      state.set(id, 1);
      for (const a of objectives.get(id)?.[0].after ?? [])
        if (objectives.has(a) && visit(a)) return true;
      state.set(id, 2);
      return false;
    };
    for (const [id, [, j]] of objectives)
      if (visit(id)) {
        fail(
          `objectives.${j}.after`,
          `${where} objective '${id}' can never be reached (its after chain loops).`,
        );
        return;
      }
  }

  function event(
    where: string,
    e: ScenarioEventDef | null | undefined,
    base: string,
    ctx: Ctx,
    fail: (path: string, m: string) => void,
    lose: boolean,
  ) {
    const at = (f: string) => `${base}.${f}`;
    if (e == null) {
      fail(base, `${where} needs an event.`);
      return;
    }
    if (!oneOf(SCENARIO_EVENT_TYPES, e.type)) {
      fail(at('type'), `${where} listens for unknown event '${e.type ?? ''}'.`);
      return;
    }
    const pct = num(e.belowPct);
    switch (e.type) {
      case 'enter_room':
        if (
          e.room &&
          !ctx.pieces.has(e.room) &&
          !templates.has(e.room) &&
          !e.room.startsWith('room:')
        )
          fail(
            at('room'),
            `${where} enters unknown room '${e.room}' (a set piece of this scenario, a room template, or room:N).`,
          );
        break;
      case 'objective':
        if (!ctx.objectives.has(e.objective ?? ''))
          fail(at('objective'), `${where} listens to unknown objective '${e.objective ?? ''}'.`);
        if (e.state && e.state !== 'revealed' && !oneOf(OBJECTIVE_STATES, e.state))
          fail(at('state'), `${where} listens for unknown objective state '${e.state}'.`);
        break;
      case 'actor_health':
        if (!ctx.actors.has(e.actor ?? ''))
          fail(at('actor'), `${where} listens to unknown actor '${e.actor ?? ''}'.`);
        if (pct < 1 || pct > 100) fail(at('belowPct'), `${where} needs belowPct in 1..100.`);
        break;
      case 'actor_dead':
      case 'reached':
        if (e.actor && !ctx.actors.has(e.actor))
          fail(at('actor'), `${where} listens to unknown actor '${e.actor}'.`);
        if (lose && !e.actor) fail(at('actor'), `${where} needs an actor.`);
        break;
      case 'timer':
        if (lose) {
          const s = num(e.s);
          const { minTimerS, maxTimerS } = SCENARIO_LIMITS;
          if (s < minTimerS || s > maxTimerS)
            fail(at('s'), `${where} needs s in ${minTimerS}..${maxTimerS}.`);
        } else if (!ctx.timers.has(e.id ?? ''))
          fail(at('id'), `${where} waits for timer '${e.id ?? ''}', which no timer action starts.`);
        break;
      case 'horde_level':
        if (pct < 1 || pct > 100)
          fail(at('belowPct'), `${where} needs the level (belowPct) in 1..100.`);
        break;
      case 'beat_outcome':
        if (!beats.has(e.id ?? ''))
          fail(at('id'), `${where} listens to unknown beat chain '${e.id ?? ''}'.`);
        break;
      case 'var':
        if (!e.id) fail(at('id'), `${where} needs the var's key (id).`);
        break;
      case 'interact':
        if (lose && !e.id) fail(at('id'), `${where} needs the prop (id).`);
        break;
    }
  }

  function action(
    where: string,
    a: ScenarioActionDef | null | undefined,
    base: string,
    ctx: Ctx,
    fail: (path: string, m: string) => void,
    serverOnly: boolean,
  ) {
    const at = (f: string) => `${base}.${f}`;
    if (a == null) {
      fail(base, `${where} has an empty action.`);
      return;
    }
    const type = a.type ?? '';
    if (!oneOf(SCENARIO_ACTION_TYPES, type)) {
      fail(at('type'), `${where} has unknown action '${type}'.`);
      return;
    }
    if (serverOnly && !oneOf(SERVER_ACTIONS, type)) {
      fail(
        at('type'),
        `${where} can only hold effect, set_flag or recruit actions (not '${type}').`,
      );
      return;
    }
    switch (type) {
      case 'beat':
        if (!beats.has(a.beatId ?? ''))
          fail(at('beatId'), `${where} plays unknown beat chain '${a.beatId ?? ''}'.`);
        break;
      case 'objective':
        if (!ctx.objectives.has(a.id ?? ''))
          fail(at('id'), `${where} sets unknown objective '${a.id ?? ''}'.`);
        if (a.set !== 'revealed' && !oneOf(OBJECTIVE_STATES, a.set))
          fail(
            at('set'),
            `${where} sets objective state '${a.set ?? ''}' (revealed, started, done, failed).`,
          );
        break;
      case 'actor':
        if (!ctx.actors.has(a.actor ?? ''))
          fail(at('actor'), `${where} moves unknown actor '${a.actor ?? ''}'.`);
        if (!oneOf(ACTOR_STATES, a.set))
          fail(at('set'), `${where} sets actor state '${a.set ?? ''}'.`);
        break;
      case 'timer': {
        const { minTimerS, maxTimerS } = SCENARIO_LIMITS;
        if (!a.id || a.id.startsWith('__'))
          fail(at('id'), `${where} needs a timer id (not starting with __).`);
        const s = num(a.s);
        if (s < minTimerS || s > maxTimerS)
          fail(at('s'), `${where} needs a timer of ${minTimerS}..${maxTimerS} s.`);
        break;
      }
      case 'bark':
        if (!ctx.actors.has(a.actor ?? ''))
          fail(at('actor'), `${where} barks for unknown actor '${a.actor ?? ''}'.`);
        if (isBlank(a.text)) fail(at('text'), `${where} needs the bark's text.`);
        break;
      case 'caption':
        if (isBlank(a.text)) fail(at('text'), `${where} needs the caption's text.`);
        break;
      case 'effect': {
        const e = a.effect as EffectDef | null | undefined;
        const et = (e?.type ?? '') as EffectType;
        if (e == null || !(EFFECT_TYPES_V1 as readonly string[]).includes(et))
          fail(at('effect'), `${where} queues unknown engine effect '${e?.type ?? ''}'.`);
        else if (e[EFFECT_BLOCKS[et]] == null)
          fail(at('effect'), `${where} effect '${et}' needs its ${et} block.`);
        break;
      }
      case 'set_flag': {
        const key = a.key ?? '';
        if (!isValidFlagKey(key, false) && !isValidFlagKey(key, true)) {
          fail(at('key'), `${where} sets a malformed world flag key '${key}'.`);
          break;
        }
        const def = findFlag(b.worldFlags, key);
        if (!def) fail(at('key'), `${where} sets world flag '${key}', which isn't in worldFlags.`);
        else {
          const problem = flagValueProblem(def, a.value ?? '');
          if (problem)
            fail(at('value'), `${where} sets '${key}' to '${a.value ?? ''}': ${problem}.`);
        }
        break;
      }
      case 'recruit':
        if (!ctx.actors.has(a.npc ?? '') && !npcs.has(a.npc ?? ''))
          fail(at('npc'), `${where} recruits unknown actor or npc '${a.npc ?? ''}'.`);
        break;
      // Card #490: the gating vocabulary (ContentValidator.Scenarios.cs, word for word).
      case 'door':
        if (!a.id) fail(at('id'), `${where} needs the door's tag (id).`);
        if (!oneOf(DOOR_OPS, a.set))
          fail(
            at('set'),
            `${where} sets door state '${a.set ?? ''}' (lock, unlock, open, seal, sealed, unsealed).`,
          );
        break;
      case 'container':
        if (!a.id) fail(at('id'), `${where} needs the container (id: a tag, #index, anchor:id or *).`);
        if (!oneOf(SWITCH_STATES, a.set))
          fail(at('set'), `${where} sets container '${a.set ?? ''}' (on or off).`);
        break;
      case 'hud':
        if (!oneOf(HUD_CONTROLS, a.id))
          fail(
            at('id'),
            `${where} switches unknown HUD control '${a.id ?? ''}' (${HUD_CONTROLS.join(', ')}).`,
          );
        if (!oneOf(SWITCH_STATES, a.set))
          fail(at('set'), `${where} sets HUD control '${a.id ?? ''}' to '${a.set ?? ''}' (on or off).`);
        break;
    }
  }

  function stages(mid: string, list: (MissionStageDef | null)[] | null | undefined, mi: number) {
    const all = list ?? [];
    if (all.length === 0) return;
    const fail = (path: string, m: string) => err('missions', mi, path, m);
    const where = `Mission '${mid}'`;
    if (all.length > SCENARIO_LIMITS.maxStages)
      fail('stages', `${where} has ${all.length} stages (at most ${SCENARIO_LIMITS.maxStages}).`);
    const ids: string[] = [];
    all.forEach((st, i) => {
      if (st == null) {
        fail(`stages.${i}`, `${where} has an empty stage.`);
        ids.push('');
        return;
      }
      if (!st.id || ids.includes(st.id))
        fail(`stages.${i}.id`, `${where} needs unique stage ids ('${st.id ?? ''}').`);
      ids.push(st.id ?? '');
    });
    all.forEach((st, i) => {
      if (st == null) return;
      const at = (f: string) => `stages.${i}.${f}`;
      const sw = `${where} stage '${st.id ?? ''}'`;
      if (isBlank(st.title)) fail(at('title'), `${sw} needs a title.`);
      const loc = st.location ?? {};
      const ltype = loc.type ?? 'building';
      if (!oneOf(STAGE_LOCATION_TYPES, ltype))
        fail(at('location.type'), `${sw} has unknown location type '${ltype}'.`);
      if (ltype === 'building') {
        (loc.categories ?? []).forEach((c, k) => {
          if (!refs.categories.has(c))
            fail(at(`location.categories.${k}`), `${sw} targets unknown category '${c}'.`);
        });
        (loc.kinds ?? []).forEach((k, n) => {
          if (k.length === 0 || normalizeBuildingKind(k) !== k)
            fail(
              at(`location.kinds.${n}`),
              `${sw} has building kind '${k}' (use lower case and _).`,
            );
        });
        const near = loc.near ?? 'player';
        if (!oneOf(STAGE_NEAR, near))
          fail(
            at('location.near'),
            `${sw} measures from unknown '${near}' (player, team or home).`,
          );
        const dist = num(loc.maxDistanceM);
        if (dist < 0 || dist > 5000)
          fail(at('location.maxDistanceM'), `${sw} needs maxDistanceM in 0..5000 (0 = 1500).`);
      }
      if (ltype === 'same_building') {
        const r = ids.indexOf(loc.ref ?? '');
        if (r < 0 || r >= i)
          fail(
            at('location.ref'),
            `${sw} goes back to '${loc.ref ?? ''}', which isn't an earlier stage.`,
          );
        else {
          const t = all[r]?.location?.type ?? 'building';
          if (t !== 'building' && t !== 'same_building' && t !== 'tip_target')
            fail(
              at('location.ref'),
              `${sw} goes back to '${loc.ref ?? ''}', which has no building.`,
            );
        }
      }
      if (ltype === 'trigger_code' && !triggerIds.has(loc.triggerId ?? ''))
        fail(at('location.triggerId'), `${sw} waits for unknown trigger '${loc.triggerId ?? ''}'.`);

      const enc = st.encounter ?? {};
      const kind = normalizeKind(enc.kind);
      if (!oneOf(TICKET_KINDS, kind) || !oneOf(PLAYABLE_KINDS, kind))
        fail(
          at('encounter.kind'),
          `${sw} plays kind '${enc.kind ?? ''}', which can't be played yet (interior or comic).`,
        );
      else if (kind === 'interior') {
        const sc = scenarioById.get(enc.scenarioId ?? '');
        if (!sc)
          fail(
            at('encounter.scenarioId'),
            `${sw} plays unknown scenario '${enc.scenarioId ?? ''}'.`,
          );
        else if (normalizeKind(sc.kind) !== 'interior')
          fail(
            at('encounter.scenarioId'),
            `${sw} plays scenario '${sc.id}' as an interior, but it's ${sc.kind ?? ''}.`,
          );
        else {
          const lc = loc.categories ?? [];
          const sc2 = sc.building?.categories ?? [];
          if (
            ltype === 'building' &&
            lc.length > 0 &&
            sc2.length > 0 &&
            !lc.some((c) => sc2.includes(c))
          )
            fail(
              at('location.categories'),
              `${sw} targets ${lc.join(', ')}, but scenario '${sc.id}' needs ${sc2.join(', ')}.`,
            );
        }
      } else if (kind === 'arena') {
        // S3-ARS: a lure-run stage plays an arena scenario at a pinned building.
        const sc = scenarioById.get(enc.scenarioId ?? '');
        if (!sc)
          fail(
            at('encounter.scenarioId'),
            `${sw} plays unknown scenario '${enc.scenarioId ?? ''}'.`,
          );
        else if (normalizeKind(sc.kind) !== 'arena')
          fail(
            at('encounter.scenarioId'),
            `${sw} plays scenario '${sc.id}' as an arena, but it's ${sc.kind ?? ''}.`,
          );
        if (ltype !== 'building' && ltype !== 'same_building')
          fail(
            at('location.type'),
            `${sw} is an arena stage: it needs a building (a lure run is played at a place).`,
          );
      } else if (kind === 'comic') {
        const beat = !!enc.beatId;
        const scn = !!enc.scenarioId;
        if (beat === scn)
          fail(
            at('encounter'),
            `${sw} is a comic stage: it needs a beatId or a comic scenarioId (one of them).`,
          );
        if (beat && !beats.has(enc.beatId ?? ''))
          fail(at('encounter.beatId'), `${sw} plays unknown beat chain '${enc.beatId}'.`);
        if (scn) {
          const sc = scenarioById.get(enc.scenarioId ?? '');
          if (!sc)
            fail(at('encounter.scenarioId'), `${sw} plays unknown scenario '${enc.scenarioId}'.`);
          else if (normalizeKind(sc.kind) !== 'comic')
            fail(
              at('encounter.scenarioId'),
              `${sw} plays scenario '${sc.id}' as a comic, but it's ${sc.kind ?? ''}.`,
            );
        }
        if (ltype === 'building' || ltype === 'same_building' || ltype === 'tip_target')
          fail(
            at('location.type'),
            `${sw} is a comic stage at a building: comic stages are played at a base, anywhere, or from a trigger.`,
          );
      }

      const onLose = st.onLose ? st.onLose : 'retry';
      if (
        onLose !== 'retry' &&
        onLose !== 'fail' &&
        onLose !== 'fail_mission' &&
        !ids.includes(onLose)
      )
        fail(at('onLose'), `${sw} has onLose '${onLose}' (retry, fail or a stage id).`);
      if (st.next && !ids.includes(st.next))
        fail(at('next'), `${sw} goes next to unknown stage '${st.next}'.`);
    });

    // The next chain from the first stage must end.
    const seen = new Set<string>();
    const byId = (id: string) => all.find((s) => s != null && s.id === id) ?? undefined;
    let cur = all.find((s) => s != null) ?? undefined;
    while (cur && cur.next) {
      if (seen.has(cur.id ?? '')) {
        fail('stages', `${where}'s stages loop (next) and never finish.`);
        break;
      }
      seen.add(cur.id ?? '');
      cur = byId(cur.next);
    }
  }
}
